<script lang="ts">
	import { yyyy, zzzz } from "../store.svelte";
	import { fly } from "svelte/transition";
	import { onMount } from "svelte";
	import Fa from "svelte-fa";
	import { faTimes, faMap } from "@fortawesome/free-solid-svg-icons";
	
	let { showLegend = $bindable(false) } = $props();
	
	let collapsed = $state(false);
	
	// Helper to get sprite image from remote iconography folder
	function getSpriteImage(spriteId: string): string | null {
		// Convert sprite ID to remote URL
		const imagePath = `https://brawta.lvwr.io/iconography/png/${spriteId}.png`;
		return imagePath;
	}
</script>

	<div id="legend-container" class="layer-card" transition:fly={{ x: 50, duration: 300 }}>
		<div class="layer-header">
			<span class="layer-title">
				<Fa icon={faMap} /> Map Legend
			</span>
			<button
				class="close-button"
				onclick={() => {
					showLegend = false;
				}}
				aria-label="close"
			>
				<Fa icon={faTimes} size="1x" />
			</button>
		</div>
		<div id="legend-content" class="layer-content">
			<!-- Lines Section -->
			<div class="legend-section">
				<h4>Electric Distribution Line</h4>
				
				<div class="legend-item">
					<svg width="40" height="20">
						<line x1="0" y1="10" x2="40" y2="10" 
							stroke="#982013" 
							stroke-width="2" 
							stroke-linecap="round" />
					</svg>
					<span>Phase A</span>
				</div>
				
				<div class="legend-item">
					<svg width="40" height="20">
						<line x1="0" y1="10" x2="40" y2="10" 
							stroke="#19950c" 
							stroke-width="2" 
							stroke-linecap="round" />
					</svg>
					<span>Phase B</span>
				</div>
				
				<div class="legend-item">
					<svg width="40" height="20">
						<line x1="0" y1="10" x2="40" y2="10" 
							stroke="#18338b" 
							stroke-width="2" 
							stroke-linecap="round" />
					</svg>
					<span>Phase C</span>
				</div>
				
				<div class="legend-item">
					<svg width="40" height="20" style="filter: blur(2px);">
						<line x1="0" y1="10" x2="40" y2="10" 
							stroke="#666666" 
							stroke-width="4" 
							stroke-linecap="round" />
					</svg>
					<span>De-energized</span>
				</div>
			</div>
			
			<!-- Electrical Devices -->
			<div class="legend-section">
				<h4>Electrical Devices</h4>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XTRA|SRCE')}" alt="Feeder" class="sprite-icon" />
					<span>Feeder</span>
				</div>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|DVCE|003|ABC')}" alt="Switchable Device" class="sprite-icon" />
					<span>Switchable Device</span>
				</div>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XFMR|004|ABC')}" alt="Transformer" class="sprite-icon" />
					<span>Transformer</span>
				</div>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XTRA|LOAD|001')}" alt="Load" class="sprite-icon" />
					<span>Load</span>
				</div>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XTRA|BLCK|003')}" alt="De-energized Device" class="sprite-icon" />
					<span>De-energized</span>
				</div>
				
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XTRA|BLCK|002')}" alt="Not Energized Device" class="sprite-icon" />
					<span>Not Energized</span>
				</div>
			</div>
			
			<div class="legend-section" class:inactive={!yyyy.map.showAdjunct}>
				<div class="legend-item">
					<img src="{getSpriteImage('LVWR|XTRA|ADJU|002')}" alt="Non-Electrical Equipment" class="sprite-icon" />
					<span>Non-Electrical Equipment</span>
				</div>
			</div>
			
			<!-- Phase Help -->
			<div class="phase-note">
				<small>Device colors indicate active phases: 
					<span style="color: #982013;">A</span>, 
					<span style="color: #19950c;">B</span>, 
					<span style="color: #18338b;">C</span>
				</small>
			</div>
			
		</div>
	</div>

<style>
	/* Use the same base styles as LayerToggle */
	.layer-card {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		right: 15px;
		z-index: 9;
		max-width: 350px;
		max-height: 75vh;
		overflow: hidden;
		border: 1px solid #dbdbdb;
		border-radius: 8.775px;
		background-color: #fff;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
		display: flex;
		flex-direction: column;
	}

	.layer-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.25rem;
		border-bottom: 1px solid #eee;
		background-color: #fff;
		position: sticky;
		top: 0;
		z-index: 1;
	}

	.layer-title {
		font-weight: bold;
		color: #363636;
		font-size: 14px;
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 0.3rem;
	}

	.close-button {
		background: transparent;
		border: none;
		color: #ff3e00;
		cursor: pointer;
		padding: 0;
		font-size: 16px;
	}

	.close-button:hover {
		color: #cc3200;
	}

	.layer-content {
		flex: 1;
		overflow-y: auto;
		padding: 1rem;
		font-size: 0.9rem;
		max-height: calc(75vh - 2rem);
	}
	
	.legend-section {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	
	.legend-section + .legend-section {
		margin-top: 20px;
	}
	
	.legend-section h4 {
		margin: 0 0 12px 0;
		font-size: 12px;
		color: #666;
		text-transform: uppercase;
		font-weight: 600;
		letter-spacing: 0.5px;
		text-align: center;
	}
	
	.legend-item {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 4px 0;
		transition: opacity 0.2s ease;
	}
	
	.legend-item.inactive {
		opacity: 0.4;
	}
	
	.legend-section.inactive {
		opacity: 0.5;
	}
	
	.legend-section.inactive h4 {
		color: #999;
	}
	
	.legend-item svg, .sprite-icon, .sprite-placeholder {
		flex-shrink: 0;
	}
	
	.sprite-icon {
		max-width: 72px;
		max-height: 36px;
		width: auto;
		height: auto;
	}
	
	.sprite-placeholder {
		width: 40px;
		height: 20px;
		background-color: #f0f0f0;
		border: 1px dashed #ccc;
	}
	
	.legend-item > span {
		font-size: 13px;
		color: #363636;
		flex: 1;
	}
	
	.phase-note {
		margin-bottom: 8px;
		padding: 4px 8px;
		background-color: #f8f9fa;
		border-radius: 4px;
	}
	
	.phase-note small {
		font-size: 11px;
		color: #666;
	}
	
	#legend-container.collapsed {
		height: auto;
		max-height: none;
	}
	
	#legend-container.collapsed .legend-content {
		display: none;
	}
	
	/* Scrollbar styling */
	#legend-container::-webkit-scrollbar {
		width: 6px;
	}
	
	#legend-container::-webkit-scrollbar-track {
		background: transparent;
	}
	
	#legend-container::-webkit-scrollbar-thumb {
		background: #cccccc;
		border-radius: 3px;
	}
	
	#legend-container::-webkit-scrollbar-thumb:hover {
		background: #999999;
	}
	
	@media only screen and (max-width: 768px) {
		#legend-container {
			max-width: 280px;
			max-height: 60vh;
			right: 10px;
		}
	}
</style>
